'use client';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Controller, useForm } from 'react-hook-form';
import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@ifa/trpc-client';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { MuiChipsInput } from 'mui-chips-input';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useRidMutation } from '../hooks/useRidMutation';
import { useDonorIdMutation } from '../hooks/useDonorIdMutation';

import { SuccessModal } from './SuccessModal';
import { PatientDataForm, PatientDataFormSchema } from './types';

export type UserData = {
    tcIds: number[];
    email: string;
    name: string;
    cm: boolean;
};

// TODO need to work off of session object
export type PatientDataFormProps = {
    userData: UserData;
};

const IconPlaceholder = () => <Box sx={{ width: '1rem', height: '1rem' }} />;

function PatientDataModal({ userData }: PatientDataFormProps) {
    const [open, setOpen] = useState(false);
    const create = api.submissionDetail.create.useMutation();
    // Did the last call result in a failure?
    const [failure, setFailure] = useState(false);
    // Is there a result to a call that our users might care about?
    const [hasResult, setHasResult] = useState(false);
    const tcIds = userData.tcIds.length ? [...userData.tcIds].sort((a, b) => a - b) : [];
    //PatientDataFormSchema expects tcId as a string
    const tcIdsStringArr = tcIds.map(tcId => String(tcId));
    const [tcIdValue, setTcIdValue] = useState(tcIdsStringArr[0]);

    const { mutateUiRid } = useRidMutation();
    const { mutateDonorId } = useDonorIdMutation();

    const handleClickOpen = () => {
        reset();
        setOpen(true);
        setFailure(false);
        setHasResult(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getEmail = (userData: UserData) => {
        if (userData.cm) {
            return 'nmdpcasemanagement@nmdp.org';
        }
        return userData.email;
    };

    const defaultEmail = getEmail(userData);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        trigger,
        getFieldState,
        getValues,
        formState: { isValid },
    } = useForm<PatientDataForm>({
        mode: 'onChange',
        reValidateMode: 'onChange',
        delayError: 200,
        defaultValues: {
            formName: 'F10', // displaying F10 as default per MAT-1495
            tcId: tcIdsStringArr[0],
            rid: '',
            email: [defaultEmail],
            donorId: '',
        },
        resolver: zodResolver(PatientDataFormSchema),
    });

    const formName = watch('formName');

    // When the Form type is changed, revalidate the donorId field to remove any lingering false error state on the field
    const revalidateGrid = () => {
        if (getFieldState('donorId').isTouched) {
            trigger('donorId');
        }
    };

    const handleSuccess = () => {
        setFailure(false);
        reset();
        handleClose();
        setHasResult(true);
    };

    const handleFailure = () => {
        setFailure(true);
        reset();
        handleClose();
        setHasResult(true);
    };
    const onSubmit = async (data: PatientDataForm) => {
        try {
            await create.mutateAsync(
                {
                    tcId: Number(data.tcId),
                    rid: data.rid,
                    grid: data.donorId,
                    formType: data.formName,
                    created: userData.email,
                    notify: data.email,
                },
                { onSuccess: () => handleSuccess(), onError: () => handleFailure() }
            );
        } catch (e) {
            console.error('Error creating submission', e);
        }
    };

    const handleCloseResultModal = () => {
        void setHasResult(false);
    };

    return (
        <div>
            <Button variant="contained" color="info" onClick={handleClickOpen} data-cy="form-request-button">
                Create form request
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth={'sm'} data-cy="form-request-dialog">
                <form onSubmit={handleSubmit(onSubmit)} data-cy="form-request-form">
                    <DialogTitle sx={{ mt: 2 }} data-cy="form-request-dialog-title">
                        Form request
                        <IconButton
                            onClick={handleClose}
                            aria-label="close"
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 5 }} data-cy="form-request-dialog-content-text">
                            To auto-populate a form with existing data, please complete the following request.
                        </DialogContentText>
                        <Grid container spacing={2}>
                            {/* Form name Dropdown */}
                            <Grid item xs={12}>
                                <Controller
                                    name="formName"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => {
                                        return (
                                            <TextField
                                                data-cy="form-request-form-name"
                                                inputProps={{
                                                    'aria-label': 'Form name',
                                                }}
                                                select
                                                required
                                                id="formName"
                                                label="Form name"
                                                variant="outlined"
                                                helperText={error?.message ?? ' '}
                                                fullWidth={true}
                                                error={!!error}
                                                {...field}
                                                onClick={revalidateGrid}
                                            >
                                                {/* TODO: Aaron - Should we keep hardcoding this list? */}
                                                <MenuItem value="F20" data-cy="F20">
                                                    F20
                                                </MenuItem>
                                                <MenuItem value="F10" data-cy="F10">
                                                    F10 Primary
                                                </MenuItem>
                                            </TextField>
                                        );
                                    }}
                                />
                            </Grid>

                            {/* TC Dropdown */}
                            <Grid item xs={12}>
                                <Controller
                                    name="tcId"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <Autocomplete
                                            {...field}
                                            id="requestTcId"
                                            data-cy="transplantCenterId"
                                            aria-label="Transplant center ID"
                                            options={tcIdsStringArr}
                                            noOptionsText="No matches"
                                            value={field.value}
                                            onChange={(_, newValue) => {
                                                field.onChange(newValue);
                                            }}
                                            onBlur={() => {
                                                if (!field.value) {
                                                    field.onChange(tcIdValue);
                                                } else {
                                                    setTcIdValue(field.value);
                                                }
                                            }}
                                            disablePortal
                                            getOptionLabel={option => option.toString()}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label="Transplant center ID"
                                                    variant="outlined"
                                                    required
                                                    fullWidth
                                                    error={!!error}
                                                    helperText={error?.message ?? ' '}
                                                    onKeyDown={ev => {
                                                        const numericKeys = [
                                                            'ArrowUp',
                                                            'ArrowDown',
                                                            'ArrowLeft',
                                                            'ArrowRight',
                                                            'Backspace',
                                                            'Enter',
                                                            'Tab',
                                                            'Digit0',
                                                            'Digit1',
                                                            'Digit2',
                                                            'Digit3',
                                                            'Digit4',
                                                            'Digit5',
                                                            'Digit6',
                                                            'Digit7',
                                                            'Digit8',
                                                            'Digit9',
                                                            'Numpad0',
                                                            'Numpad1',
                                                            'Numpad2',
                                                            'Numpad3',
                                                            'Numpad4',
                                                            'Numpad5',
                                                            'Numpad6',
                                                            'Numpad7',
                                                            'Numpad8',
                                                            'Numpad9',
                                                        ];
                                                        if (!numericKeys.includes(ev.code)) {
                                                            ev.preventDefault();
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* RID Input Field */}
                            <Grid item xs={12} data-cy="form-request-rid-item">
                                <Controller
                                    name="rid"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputProps={{
                                                'data-cy': 'patientRid',
                                                'aria-label': 'Patient ID',
                                            }}
                                            required
                                            label="Patient ID"
                                            variant="outlined"
                                            fullWidth
                                            onChange={event => {
                                                field.onChange(mutateUiRid(event.target.value));
                                            }}
                                            error={!!error}
                                            helperText={error?.message ?? ' '}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* Donor GRID Input Field */}
                            <Grid item xs={12} data-cy="form-request-donor-id">
                                <Controller
                                    name="donorId"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <TextField
                                            {...field}
                                            inputProps={{
                                                'data-cy': 'grid',
                                                'aria-label': 'GRID',
                                            }}
                                            label="GRID"
                                            variant="outlined"
                                            required={formName === 'F10'}
                                            fullWidth
                                            onChange={event => {
                                                field.onChange(mutateDonorId(event.target.value));
                                            }}
                                            error={!!error}
                                            helperText={error?.message ?? ' '}
                                        />
                                    )}
                                />
                            </Grid>
                            {/* Email Input Field */}
                            <Grid item xs={12} data-cy="notification-email-addresses-item">
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState: { error } }) => (
                                        <MuiChipsInput
                                            // The label below manually adds a red asterisk to indicate required without interfering with the form validation.
                                            // Just adding "required" doesn't play nicely with MuiChipsInput
                                            label={[
                                                'Notification email addresses',
                                                <span
                                                    key={'email'}
                                                    aria-hidden="true"
                                                    className="MuiFormLabel-asterisk MuiInputLabel-asterisk mui-wdivi1-MuiFormLabel-asterisk"
                                                >
                                                    {' '}
                                                    *
                                                </span>,
                                            ]}
                                            {...field}
                                            hideClearAll
                                            fullWidth
                                            error={!!error}
                                            helperText={
                                                'Please provide at least one email address as the recipient. You can add more recipients, but they must have the necessary access permissions to download the forms.'
                                            }
                                            data-cy="email"
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Alert severity="info">
                                    The form will be created using data provided to NMDP. Upon completion, it is the
                                    responsibility of the transplant center to verify that all prepopulated fields are
                                    accurate.
                                </Alert>
                            </Grid>
                            {/* F10 Primary Only Field */}
                            {getValues().formName === 'F10' && (
                                <Grid item xs={12}>
                                    <Controller
                                        name="primaryOnly"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!value}
                                                        onChange={onChange}
                                                        data-cy="primary-only-checkbox"
                                                        inputProps={{
                                                            'aria-label': 'Primary workup order',
                                                        }}
                                                    />
                                                }
                                                label="By checking this box I understand that the workup data I am requesting is for an initial workup order for this donor."
                                                labelPlacement="end"
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ mb: 2, mr: 2, ml: 2, justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            fullWidth
                            type="submit"
                            disabled={!isValid || create.isPending}
                            data-cy="form-request-submit-button"
                            endIcon={
                                create.isPending ? (
                                    <CircularProgress size={'1rem'} color="primary" />
                                ) : (
                                    <IconPlaceholder />
                                )
                            }
                        >
                            Submit form request
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
            {hasResult && (
                <SuccessModal
                    onClose={handleCloseResultModal}
                    success={true}
                    title={failure ? 'Failed to submit' : 'Successfully submitted'}
                >
                    {failure
                        ? 'Failed to submit the form request. Please try again later.'
                        : "Your auto-population request has been successfully submitted for processing. You can check the status of your request in the 'Submitted form requests' section. Once processing is complete and the status is marked as 'Ready', the form will be available for download."}
                </SuccessModal>
            )}
        </div>
    );
}

export default PatientDataModal;
